import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '../lib/supabase';
import { getCurrentUser } from '../lib/auth';
import toast from 'react-hot-toast';
import { FaHeart, FaLanguage, FaBook, FaPlay, FaStar, FaFire, FaClock, FaTrophy, FaLock, FaUnlock, FaCrown, FaDumbbell, FaVolumeUp, FaExternalLinkAlt, FaBullhorn, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import MusicBackground from '../components/MusicBackground';
import ReportButton from '../components/reports/ReportButton';

interface Song {
  id: string;
  song_url: string;
  song_cover: string;
  creator: string;
  language: 'english' | 'spanish' | 'french';
  song_lyrics: string;
  translation: string | null;
  song_vocabulary: any;
  song_likes: number;
  is_promoted: boolean;
  is_featured: boolean;
  is_free: boolean;
  user_has_liked?: boolean;
}

interface Ad {
  id: string;
  image: string;
  title: string;
  description: string;
  link: string;
  is_active: boolean;
  created_at: string;
}

export default function Dashboard() {
  const [promotedSongs, setPromotedSongs] = useState<Song[]>([]);
  const [featuredSongs, setFeaturedSongs] = useState<Song[]>([]);
  const [popularSongs, setPopularSongs] = useState<Song[]>([]);
  const [newSongs, setNewSongs] = useState<Song[]>([]);
  const [ads, setAds] = useState<Ad[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [subscriptionDaysLeft, setSubscriptionDaysLeft] = useState<number | null>(null);
  const [isPracticeModeExpanded, setIsPracticeModeExpanded] = useState(false);
  const navigate = useNavigate();
  const user = getCurrentUser();

  useEffect(() => {
    loadSongs();
    loadAds();
    checkAccess();
    checkSubscriptionStatus();
  }, []);

  const checkAccess = async () => {
    if (!user) return;

    try {
      if (user.is_admin) {
        setHasAccess(true);
        return;
      }

      const { data: couponUsage, error: couponError } = await supabase
        .from('stripecouponusage')
        .select('*')
        .eq('user_id', user.id)
        .gte('valid_until', new Date().toISOString());

      if (couponError && couponError.code !== 'PGRST116') {
        throw couponError;
      }

      setHasAccess(!!couponUsage && couponUsage.length > 0);
    } catch (error) {
      console.error('Error checking access:', error);
      setHasAccess(false);
    }
  };

  const checkSubscriptionStatus = async () => {
    try {
      // Check if user is authenticated first
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.log('No authenticated user found');
        return;
      }

      const { data, error } = await supabase
        .from('stripecouponusage')
        .select('valid_until') 
        .eq('user_id', user.id)
        .gt('valid_until', new Date().toISOString())
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No subscription found - this is normal
          console.log('No active subscription found');
        } else {
          console.error('Error checking subscription:', error);
        }
        return;
      }

      if (data) {
        setHasSubscription(true);
        
        // Calculate days left
        const validUntil = new Date(data.valid_until);
        const today = new Date();
        const diffTime = validUntil.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        setSubscriptionDaysLeft(diffDays);
      } else {
        setHasSubscription(false);
        setSubscriptionDaysLeft(null);
      }
    } catch (error) {
      console.error('Network error checking subscription:', error);
      // Don't throw the error, just log it
    }
  };

  const loadAds = async () => {
    try {
      const { data, error } = await supabase
        .from('stripeads')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Randomly select 2 ads if there are more than 2
      const allAds = data || [];
      if (allAds.length <= 2) {
        setAds(allAds);
      } else {
        // Shuffle the ads array and take the first 2
        const shuffledAds = [...allAds].sort(() => Math.random() - 0.5);
        setAds(shuffledAds.slice(0, 2));
      }
    } catch (error) {
      console.error('Error loading ads:', error);
    }
  };

  const loadSongs = async () => {
    try {
      const userId = user?.id;

      // Load promoted songs
      const { data: promotedData } = await supabase
        .from('stripesongs')
        .select('*')
        .eq('is_promoted', true)
        .eq('is_visible', true)
        .limit(3);

      // Load featured songs
      const { data: featuredData } = await supabase
        .from('stripesongs')
        .select('*')
        .eq('is_featured', true)
        .eq('is_visible', true)
        .limit(3);

      // Load most liked songs
      const { data: popularData } = await supabase
        .from('stripesongs')
        .select('*')
        .eq('is_visible', true)
        .order('song_likes', { ascending: false })
        .limit(3);

      // Load newest songs
      const { data: newData } = await supabase
        .from('stripesongs')
        .select('*')
        .eq('is_visible', true)
        .order('created_at', { ascending: false })
        .limit(3);

      // If user is logged in, check which songs they've liked
      if (userId) {
        const allSongs = [...(promotedData || []), ...(featuredData || []), ...(popularData || []), ...(newData || [])];
        const songIds = allSongs.map(song => song.id);

        if (songIds.length > 0) {
          const { data: likedSongs } = await supabase
            .from('stripe_like_songs')
            .select('song_id')
            .eq('user_id', userId)
            .in('song_id', songIds);

          const likedSongIds = new Set(likedSongs?.map(like => like.song_id) || []);

          // Add user_has_liked property to each song
          const addLikeStatus = (songs: Song[]) =>
            songs.map(song => ({
              ...song,
              user_has_liked: likedSongIds.has(song.id)
            }));

          setPromotedSongs(addLikeStatus(promotedData || []));
          setFeaturedSongs(addLikeStatus(featuredData || []));
          setPopularSongs(addLikeStatus(popularData || []));
          setNewSongs(addLikeStatus(newData || []));
        } else {
          setPromotedSongs(promotedData || []);
          setFeaturedSongs(featuredData || []);
          setPopularSongs(popularData || []);
          setNewSongs(newData || []);
        }
      } else {
        setPromotedSongs(promotedData || []);
        setFeaturedSongs(featuredData || []);
        setPopularSongs(popularData || []);
        setNewSongs(newData || []);
      }
    } catch (error) {
      console.error('Error loading songs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async (songId: string) => {
    if (!user) {
      toast.error('Please log in to like songs');
      navigate('/login');
      return;
    }

    try {
      // Check if user has already liked this song
      const { data: existingLike, error: checkError } = await supabase
        .from('stripe_like_songs')
        .select('id')
        .eq('song_id', songId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (checkError && checkError.code !== 'PGRST116') {
        throw checkError;
      }

      if (existingLike) {
        // User has already liked this song, remove the like
        const { error: deleteError } = await supabase
          .from('stripe_like_songs')
          .delete()
          .eq('song_id', songId)
          .eq('user_id', user.id);

        if (deleteError) throw deleteError;

        // Decrease like count in songs table
        const { error: updateError } = await supabase
          .rpc('decrement_ensong_likes', { song_id: songId });

        if (updateError) throw updateError;

        // Update UI to reflect unlike
        const updateSongs = (songs: Song[]) =>
          songs.map(s => s.id === songId ? { ...s, song_likes: s.song_likes - 1, user_has_liked: false } : s);

        setPromotedSongs(updateSongs);
        setFeaturedSongs(updateSongs);
        setPopularSongs(updateSongs);
        setNewSongs(updateSongs);

        toast.success('Like removed!');
      } else {
        // User hasn't liked this song, add a like
        const { error: insertError } = await supabase
          .from('stripe_like_songs')
          .insert({
            song_id: songId,
            user_id: user.id
          });

        if (insertError) throw insertError;

        // Increase like count in songs table
        const { error: updateError } = await supabase
          .rpc('increment_ensong_likes', { song_id: songId });

        if (updateError) throw updateError;

        // Update UI to reflect like
        const updateSongs = (songs: Song[]) =>
          songs.map(s => s.id === songId ? { ...s, song_likes: s.song_likes + 1, user_has_liked: true } : s);

        setPromotedSongs(updateSongs);
        setFeaturedSongs(updateSongs);
        setPopularSongs(updateSongs);
        setNewSongs(updateSongs);

        toast.success('Song liked!');
      }
    } catch (error) {
      console.error('Error handling like:', error);
      toast.error('Failed to update like');
    }
  };

  const handlePlay = (song: Song) => {
    if (!user) {
      toast.error('Please log in to access songs');
      navigate('/login');
      return;
    }

    if (!hasAccess && !song.is_free) {
      setShowAccessModal(true);
      return;
    }

    navigate(`/songs/${song.id}`);
  };

  const handleBuyCoupon = () => {
    navigate('/coupons');
  };

  const SongCard: React.FC<{ song: Song }> = ({ song }) => (
    <motion.div 
      className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-xl transition-all duration-300 group border border-gray-100 hover:border-yellow-300 cursor-pointer"
      whileHover={{ scale: 1.03, y: -4 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => handlePlay(song)}
    >
      {/* Compact Card Layout */}
      <div className="flex sm:flex-col">
        {/* Image Section - Side on mobile, top on desktop */}
        <div className="relative w-20 h-20 sm:w-full sm:h-28 md:h-32 flex-shrink-0">
          <img
            src={song.song_cover}
            alt={song.creator}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          />
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          
          {/* Play button overlay */}
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
            {hasAccess || song.is_free ? (
              <motion.div 
                className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-yellow-400 flex items-center justify-center shadow-lg"
                initial={{ scale: 0 }}
                whileHover={{ scale: 1.1 }}
              >
                <FaPlay className="w-3 h-3 sm:w-4 sm:h-4 text-black ml-0.5" />
              </motion.div>
            ) : (
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-black/70 flex items-center justify-center">
                <FaLock className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
              </div>
            )}
          </div>

          {/* Badges - Compact */}
          <div className="absolute top-1 right-1 flex flex-col gap-1">
            {song.is_promoted && (
              <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 p-1 sm:p-1.5 rounded-full shadow-md" title="Premium">
                <FaCrown className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-black" />
              </div>
            )}
            {song.is_free && (
              <div className="bg-gradient-to-r from-green-400 to-green-500 p-1 sm:p-1.5 rounded-full shadow-md" title="Free">
                <FaUnlock className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />
              </div>
            )}
          </div>
        </div>

        {/* Content Section */}
        <div className="flex-1 p-2 sm:p-3 min-w-0">
          {/* Song Name - Prominent */}
          <h3 className="font-bold text-gray-900 text-sm sm:text-base leading-tight truncate group-hover:text-yellow-600 transition-colors duration-200 mb-1">
            {song.creator}
          </h3>
          
          {/* Language badge - Icon only on mobile */}
          <div className="flex items-center gap-1.5 mb-1.5 sm:mb-2">
            <span className="inline-flex items-center px-1.5 py-0.5 bg-indigo-50 text-indigo-700 rounded text-xs font-medium">
              <FaLanguage className="w-3 h-3 sm:mr-1" />
              <span className="hidden sm:inline">{song.language}</span>
            </span>
          </div>

          {/* Actions Row */}
          <div className="flex items-center justify-between">
            {/* Like Button */}
            <button 
              onClick={(e) => {
                e.stopPropagation();
                handleLike(song.id);
              }}
              className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-all duration-200 ${
                song.user_has_liked 
                  ? 'bg-yellow-50 text-yellow-600' 
                  : 'bg-gray-50 text-gray-500 hover:bg-red-50 hover:text-red-500'
              }`}
            >
              <FaHeart className={`w-3 h-3 ${song.user_has_liked ? 'text-yellow-500' : ''}`} />
              <span>{song.song_likes}</span>
            </button>
            
            {/* Feature Icons */}
            <div className="flex items-center gap-1">
              {song.translation && (
                <div className="p-1 rounded bg-gray-50 text-gray-400 hover:text-indigo-500 transition-colors" title="Translation">
                  <FaLanguage className="w-3 h-3" />
                </div>
              )}
              {song.song_vocabulary && (
                <div className="p-1 rounded bg-gray-50 text-gray-400 hover:text-indigo-500 transition-colors" title="Vocabulary">
                  <FaBook className="w-3 h-3" />
                </div>
              )}
              <div 
                onClick={(e) => e.stopPropagation()}
                className="p-1 rounded bg-gray-50"
              >
                <ReportButton
                  contentId={song.id}
                  contentPreview={`Song: ${song.creator} - ${song.song_lyrics?.substring(0, 100) || 'No lyrics available'}...`}
                  variant="B"
                  className="text-gray-400 hover:text-red-500 !p-0"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );

  const SongSection: React.FC<{
    title: string;
    icon: React.ReactNode;
    songs: Song[];
    loading: boolean;
    className?: string;
  }> = ({ title, icon, songs, loading, className }) => {
    const isPromoted = title === "Promoted Songs";
    
    return (
      <div className={`${className} ${isPromoted ? 'promoted-songs-container' : ''}`}>
        {/* Premium Floating Particles for Promoted Songs */}
        {isPromoted && (
          <div className="premium-particles">
            {[...Array(15)].map((_, i) => (
              <div
                key={i}
                className="premium-particle"
                style={{
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 8}s`,
                  animationDuration: `${6 + Math.random() * 4}s`
                }}
              />
            ))}
          </div>
        )}
        
        <div className={`flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6 relative ${isPromoted ? 'premium-header' : ''} ${isPromoted ? 'p-3 sm:p-4 rounded-xl sm:rounded-2xl' : ''}`}>
          <div className={`p-2 sm:p-3 rounded-lg shadow-lg ${
            isPromoted 
              ? 'premium-icon' 
              : 'bg-gradient-to-r from-yellow-400 to-yellow-600'
          }`}>
            <div className="w-4 h-4 sm:w-6 sm:h-6 flex items-center justify-center">
              {icon}
            </div>
          </div>
          <h2 className={`text-lg sm:text-2xl font-bold relative section-header-glow ${
            isPromoted 
              ? 'premium-title' 
              : 'text-gray-900'
          }`}>
            <span className="hidden sm:inline">{title}</span>
            <span className="sm:hidden">
              {title === "Promoted Songs" && "Premium"}
              {title === "Featured Songs" && "Featured"}
              {title === "Most Popular" && "Popular"}
              {title === "Recently Added" && "New"}
            </span>
            {!isPromoted && (
              <span className="absolute -bottom-1 left-0 w-full h-0.5 sm:h-1 bg-gradient-to-r from-yellow-400 to-transparent rounded-full"></span>
            )}
          </h2>
          {isPromoted && (
            <div className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 hidden sm:block">
              <div className="relative">
                <FaStar className="text-yellow-400 w-4 h-4 sm:w-6 sm:h-6 animate-pulse absolute -top-1 -left-1 drop-shadow-lg" />
                <FaStar className="text-yellow-300 w-3 h-3 sm:w-5 sm:h-5 animate-pulse absolute top-1 -right-1 drop-shadow-lg" />
              </div>
            </div>
          )}
          <div className="ml-auto">
            <Link 
              to="/songs" 
              className={`text-xs sm:text-sm font-medium flex items-center gap-1 transition-all duration-300 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg ${
                isPromoted 
                  ? 'premium-plus-link text-yellow-400 hover:text-yellow-300 bg-black/20' 
                  : 'text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100'
              }`}
            >
              <span className="hidden sm:inline">See all</span>
              <FaPlay className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
            </Link>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
          {loading ? (
            Array(4).fill(0).map((_, i) => (
              <div key={i} className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="flex sm:flex-col">
                  <Skeleton width={80} height={80} className="sm:!w-full sm:!h-28" />
                  <div className="p-2 sm:p-3 flex-1">
                    <Skeleton width="80%" height={16} />
                    <Skeleton width="40%" height={12} className="mt-1" />
                    <Skeleton width="60%" height={20} className="mt-2" />
                  </div>
                </div>
              </div>
            ))
          ) : songs.length > 0 ? (
            songs.map((song) => (
              <SongCard key={song.id} song={song} />
            ))
          ) : (
            <div className="col-span-full text-center py-6 text-gray-500 text-sm">
              No songs available in this section
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="py-8 px-4 sm:px-6 lg:px-8 relative bg-gray-100">
      <MusicBackground />
      
      {/* Welcome Section */}
      <div className="mb-6 sm:mb-10 text-center relative">
        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-2 sm:mb-3 relative inline-block">
          Welcome to Language Learning
          <span className="absolute -bottom-1 left-0 w-full h-0.5 sm:h-1 bg-gradient-to-r from-yellow-400 to-transparent rounded-full"></span>
        </h1>
        <p className="text-sm sm:text-base text-gray-600 max-w-xl mx-auto px-2">
          Discover new songs, improve your language skills, and enjoy learning through music.
        </p>
      </div>

      {/* Practice Mode Banner - Collapsible */}
      <div className="mb-6 sm:mb-8">
        <motion.div 
          className="relative bg-gradient-to-br from-black via-gray-900 to-black rounded-xl sm:rounded-2xl shadow-lg sm:shadow-2xl overflow-hidden border border-gray-800"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Compact Header - Always Visible */}
          <button
            onClick={() => setIsPracticeModeExpanded(!isPracticeModeExpanded)}
            className="w-full p-3 sm:p-4 flex items-center justify-between hover:bg-white/5 transition-colors duration-200"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-lg sm:rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
                <FaDumbbell className="w-5 h-5 sm:w-6 sm:h-6 text-black" />
              </div>
              <div className="text-left">
                <h2 className="text-base sm:text-xl font-bold text-white flex items-center gap-2">
                  Practice Mode
                  {hasSubscription && (
                    <span className="inline-flex items-center bg-gradient-to-r from-yellow-400 to-yellow-600 text-black text-[10px] sm:text-xs px-2 py-0.5 rounded-full font-bold">
                      {subscriptionDaysLeft}d left
                    </span>
                  )}
                </h2>
                <p className="text-gray-400 text-xs sm:text-sm hidden sm:block">Interactive exercises & AI feedback</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Quick Action - Always visible */}
              <Link
                to="/practice"
                onClick={(e) => e.stopPropagation()}
                className="px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black rounded-lg font-bold text-xs sm:text-sm hover:from-yellow-500 hover:to-yellow-600 transition-all duration-200 shadow-md"
              >
                <span className="hidden sm:inline">Start</span>
                <FaPlay className="w-3 h-3 sm:hidden" />
              </Link>
              
              {/* Expand/Collapse Icon */}
              <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                <motion.div
                  animate={{ rotate: isPracticeModeExpanded ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <FaChevronDown className="w-4 h-4 text-white" />
                </motion.div>
              </div>
            </div>
          </button>

          {/* Expandable Content */}
          <motion.div
            initial={false}
            animate={{ 
              height: isPracticeModeExpanded ? 'auto' : 0,
              opacity: isPracticeModeExpanded ? 1 : 0
            }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="px-3 sm:px-6 pb-4 sm:pb-6 pt-2 border-t border-gray-800/50">
              {/* Feature Tags */}
              <div className="flex flex-wrap gap-2 mb-4">
                {[
                  { icon: FaBook, label: 'Vocabulary', color: 'bg-blue-500/20 border-blue-500/30 text-blue-300' },
                  { icon: FaLanguage, label: 'Conjugation', color: 'bg-purple-500/20 border-purple-500/30 text-purple-300' },
                  { icon: FaVolumeUp, label: 'Listening', color: 'bg-green-500/20 border-green-500/30 text-green-300' }
                ].map((item) => (
                  <div
                    key={item.label}
                    className={`flex items-center ${item.color} px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full border text-xs sm:text-sm font-medium`}
                  >
                    <item.icon className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5" />
                    <span>{item.label}</span>
                  </div>
                ))}
              </div>

              {/* Action Buttons Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                <Link
                  to="/practice"
                  className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 sm:py-3 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black rounded-lg sm:rounded-xl font-bold text-xs sm:text-sm hover:from-yellow-500 hover:to-yellow-600 transition-all duration-200 shadow-md"
                >
                  <FaDumbbell className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span>Practice</span>
                </Link>
                
                <Link
                  to="/challenge"
                  className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 sm:py-3 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg sm:rounded-xl font-bold text-xs sm:text-sm hover:from-purple-600 hover:to-purple-700 transition-all duration-200 shadow-md"
                >
                  <FaTrophy className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span>Challenge</span>
                </Link>
                
                <Link
                  to="/leaderboard"
                  className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 sm:py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg sm:rounded-xl font-bold text-xs sm:text-sm hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-md col-span-2 sm:col-span-1"
                >
                  <FaCrown className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span>Leaderboard</span>
                </Link>
              </div>

              {/* Free Trial Note */}
              {!hasSubscription && (
                <p className="mt-3 text-gray-400 text-xs sm:text-sm text-center">
                  Try our 1-day free trial!
                </p>
              )}
            </div>
          </motion.div>
        </motion.div>
      </div>

      <div className="space-y-12">
        {/* Promoted Songs Section - Customized */}
        <SongSection
          title="Promoted Songs"
          icon={<FaStar className="w-6 h-6 text-yellow-500" />}
          songs={promotedSongs}
          loading={loading}
        />

        <SongSection
          title="Featured Songs"
          icon={<FaFire className="w-6 h-6 text-red-500" />}
          songs={featuredSongs}
          loading={loading}
        />

        {/* Ads Banner - After Featured Songs */}
        {ads.length > 0 && (
          <div className="relative overflow-hidden bg-gradient-to-br from-black via-gray-900 to-black p-6 rounded-2xl shadow-2xl border border-gray-800">
            {/* Animated Background Elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <motion.div 
                className="absolute -top-10 -right-10 w-32 h-32 bg-gradient-to-br from-blue-400/10 to-purple-400/10 rounded-full blur-3xl"
                animate={{ 
                  scale: [1, 1.2, 1],
                  rotate: [0, 180, 360]
                }}
                transition={{ 
                  duration: 8,
                  repeat: Infinity,
                  ease: "linear"
                }}
              />
              <motion.div 
                className="absolute -bottom-10 -left-10 w-24 h-24 bg-gradient-to-br from-purple-400/10 to-blue-400/10 rounded-full blur-3xl"
                animate={{ 
                  scale: [1.2, 1, 1.2],
                  rotate: [360, 180, 0]
                }}
                transition={{ 
                  duration: 10,
                  repeat: Infinity,
                  ease: "linear"
                }}
              />
            </div>

            <div className="flex items-center space-x-3 mb-4 relative z-10">
              <div className="p-2 rounded-lg bg-white shadow-lg">
                <FaBullhorn className="w-5 h-5 text-gray-900" />
              </div>
              <h2 className="text-xl font-bold text-white relative">
               Ads
                <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-gradient-to-r from-blue-400 to-transparent rounded-full animate-pulse"></span>
              </h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 relative z-10">
              {ads.map((ad) => (
                <motion.div
                  key={ad.id}
                  className="bg-white/10 backdrop-blur-lg rounded-lg overflow-hidden hover:bg-white/20 transition-all duration-300 hover:-translate-y-1 group border border-white/20 hover:border-blue-400/50"
                  whileHover={{ scale: 1.02 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={ad.image}
                      alt={ad.title}
                      className="w-full h-32 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="absolute top-1 right-1 bg-blue-500 text-white px-2 py-1 rounded-full text-xs font-bold shadow-lg">
                      Sponsored
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="text-base font-semibold text-white mb-2 group-hover:text-blue-400 transition-colors duration-200 line-clamp-1">
                      {ad.title}
                    </h3>
                    <p className="text-gray-300 mb-3 text-xs leading-relaxed line-clamp-2">
                      {ad.description}
                    </p>
                    <a
                      href={ad.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-3 py-2 bg-black text-white rounded-lg hover:bg-gray-900 transition-all duration-200 transform hover:scale-105 shadow-md text-xs font-medium"
                    >
                      Learn More
                      <FaExternalLinkAlt className="ml-1 w-3 h-3" />
                    </a>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        <SongSection
          title="Most Popular"
          icon={<FaTrophy className="w-6 h-6 text-purple-500" />}
          songs={popularSongs}
          loading={loading}
        />

        <SongSection
          title="Recently Added"
          icon={<FaClock className="w-6 h-6 text-green-500" />}
          songs={newSongs}
          loading={loading}
        />
      </div>

      {/* Access Required Modal */}
      {showAccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Access Required</h3>
            </div>
            <div className="p-6">
              <div className="mb-6">
                <p className="text-gray-600">
                  To access all songs and learning materials, you need to purchase a coupon worth $12.
                  Each coupon provides 30 days of unlimited access to all content on the website.
                </p>
              </div>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => setShowAccessModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBuyCoupon}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Buy Coupon
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}